import { useEffect, useState } from "react";
import { Plus, Pencil, Hospital as HospitalIcon } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { GST_STATES } from "../utils/gstStates";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { useLang } from "../i18n";

const empty = { state_code: "10", name: "", address: "", contact_person: "", gstin: "", phone: "", email: "", type: "Government", credit_limit: 0, pincode: "" };

export default function Hospitals() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [q, setQ] = useState("");
  const { t } = useLang();

  const load = async () => setRows((await api.get("/hospitals", { params: { q } })).data);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load().catch(() => {}); }, [q]);

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, credit_limit: Number(form.credit_limit) || 0 };
    try {
      if (editId) await api.put(`/hospitals/${editId}`, payload);
      else await api.post("/hospitals", payload);
      toast.success(editId ? "Hospital updated" : "Hospital added");
      setOpen(false); setForm(empty); setEditId(null); load();
    } catch (err) { toast.error(err.message); }
  };

  return (
    <div data-testid="hospitals-page" className="sgp-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="sgp-page-title">{t("hospitals")}</h1>
          <p className="sgp-page-subtitle">Hospital / PHC / Institution master — quotation aur bill mein reuse hoga.</p>
        </div>
        <button data-testid="add-hospital-btn" onClick={() => { setForm(empty); setEditId(null); setOpen(true); }}
          className="sgp-primary-btn">
          <Plus className="h-4 w-4" /> {t("add")}
        </button>
      </div>

      <input data-testid="hospitals-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search hospital by name..." className="sgp-input max-w-sm" />

      <div className="sgp-table-shell overflow-x-auto">
        <table className="w-full min-w-[760px]">
          <thead>
            <tr>
              <th className="sgp-th">Hospital / Institution</th><th className="sgp-th">Type</th><th className="sgp-th">GSTIN</th>
              <th className="sgp-th">Contact Person</th><th className="sgp-th">Phone</th><th className="sgp-th"></th>
            </tr>
          </thead>
          <tbody data-testid="hospitals-table">
            {rows.map((h) => (
              <tr key={h.id} data-testid={`hospital-row-${h.id}`} className="sgp-table-row">
                <td className="sgp-td">
                  <div className="flex items-center gap-2.5">
                    <div className="h-8 w-8 rounded-md bg-blue-50 border border-blue-100 flex items-center justify-center">
                      <HospitalIcon className="h-4 w-4 text-blue-700" />
                    </div>
                    <div><p className="font-semibold text-slate-800">{h.name}</p><p className="text-xs text-slate-400">{h.address}</p></div>
                  </div>
                </td>
                <td className="sgp-td"><span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${h.type === "Government" ? "bg-indigo-50 text-indigo-700" : "bg-amber-50 text-amber-700"}`}>{h.type}</span></td>
                <td className="sgp-td font-mono text-xs">{h.gstin || "-"}</td>
                <td className="sgp-td text-xs">{h.contact_person || "-"}</td>
                <td className="sgp-td text-xs">{h.phone || "-"}</td>
                <td className="sgp-td">
                  <button data-testid={`edit-hospital-${h.id}`} onClick={() => { setForm({ ...empty, ...h }); setEditId(h.id); setOpen(true); }}
                    className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-teal-700"><Pencil className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={6} className="sgp-td text-center py-10 text-slate-400">Koi hospital nahi mila.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="hospital-form-modal" className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Edit Hospital" : "Add Hospital / Institution"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-2 gap-3 mt-2">
            <div className="col-span-2"><label className="sgp-label">Name *</label>
              <input data-testid="hospital-name-input" required className="sgp-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="col-span-2"><label className="sgp-label">Address</label>
              <input data-testid="hospital-address-input" className="sgp-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div><label className="sgp-label">Type</label>
              <select data-testid="hospital-type-input" className="sgp-input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                <option>Government</option><option>Private</option><option>PHC / CHC</option><option>Nursing Home</option><option>GeM / Tender</option>
              </select></div>
            <div><label className="sgp-label">Contact Person</label>
              <input data-testid="hospital-contact-input" className="sgp-input" value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></div>
            <div><label className="sgp-label">Supply State</label><select data-testid="hospital-state-select" className="sgp-input" value={form.state_code || "10"} onChange={e => setForm({ ...form, state_code: e.target.value })}>{Object.entries(GST_STATES).map(([code, name]) => <option key={code} value={code}>{code} — {name}</option>)}</select></div>
            <div><label className="sgp-label">GSTIN (agar ho)</label>
              <input data-testid="hospital-gstin-input" className="sgp-input font-mono" maxLength={15} value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })} /></div>
            <div><label className="sgp-label">Phone</label>
              <input data-testid="hospital-phone-input" className="sgp-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div><label className="sgp-label">Email</label>
              <input data-testid="hospital-email-input" type="email" className="sgp-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><label className="sgp-label">Pincode</label>
              <input data-testid="hospital-pincode-input" className="sgp-input font-mono" value={form.pincode || ""} onChange={(e) => setForm({ ...form, pincode: e.target.value })} /></div>
            <div className="col-span-2"><label className="sgp-label">Credit Limit (₹) — isse zyada udhaar pe billing warning aayegi</label>
              <input data-testid="hospital-credit-limit-input" type="number" className="sgp-input font-mono" value={form.credit_limit || 0} onChange={(e) => setForm({ ...form, credit_limit: e.target.value })} /></div>
            <div className="col-span-2 flex justify-end gap-2 mt-1">
              <button type="button" data-testid="hospital-cancel-btn" onClick={() => setOpen(false)} className="px-4 py-2 text-sm rounded-md border border-slate-300 hover:bg-slate-50">{t("cancel")}</button>
              <button type="submit" data-testid="hospital-save-btn" className="sgp-primary-btn">{t("save")}</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

